// Lib
import React, { useRef } from 'react';
import { X } from 'lucide-react';

// Hooks
import usePopup from '../hooks/usePopup';

// Components
import Backdoor from './Backdoor';
import { Button } from '@components/ui/button';
import { cn } from '@components/lib/utils';

const isSafari = globalThis?.safari !== undefined;

const BODY_BASE =
  '!absolute inset-0 m-auto h-fit max-h-[90vh] w-fit max-w-[460px] overflow-x-hidden overflow-y-auto [overscroll-behavior:contain] [&::-webkit-scrollbar]:hidden max-[480px]:w-[95%] max-[743px]:m-[auto_auto_0] max-[743px]:w-full max-[743px]:!rounded-t-[20px]';

// Full-screen variant on tablet/mobile (dark sheet). Currently unused — every
// call site passes `isMobileFullSize: false`. Its hardcoded navy/white would
// need the same token treatment as the rest of this file before it is revived.
const BODY_FULLSIZE =
  'border border-white bg-[#080c2b] max-[1024px]:m-[0_auto] max-[1024px]:h-full max-[1024px]:w-full max-[1024px]:max-w-full max-[1024px]:!rounded-none max-[1024px]:border-none';

const Popup: React.FC = () => {
  const {
    id = 'popup',
    display,
    title,
    style,
    children,
    onCancel,
    onDone,
    animation,
    closable,
    positiveText,
    negativeText,
    top,
    isMobileFullSize,
  } = usePopup();

  const popUpRef = useRef<HTMLDivElement>(null);
  const onHandleCancel = () => {
    if (!isSafari) {
      const outerPopup = popUpRef.current;
      const popup = outerPopup?.querySelector(
        '.card-modal',
      ) as HTMLElement | null;
      if (popup) {
        popup.classList.add('animate-out');
        const handleAnimationEnd = function () {
          popup.style.display = 'none';
          popup.classList.remove('animate-out');
          popup.removeEventListener('animationend', handleAnimationEnd);
          onCancel();
        };
        popup.addEventListener('animationend', handleAnimationEnd);
      } else {
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  return !display ? null : (
    <Backdoor
      popUpRef={popUpRef}
      id={id ?? undefined}
      onClick={closable ? onHandleCancel : () => {}}
      top={parseInt(top, 10) || 0}
      bodyStyle={style ?? undefined}
      animation={animation}
      tone={0.4}
      bodyClassName={cn(BODY_BASE, isMobileFullSize && BODY_FULLSIZE)}
    >
      {closable && !positiveText && !negativeText && (
        <div
          onClick={onHandleCancel}
          className="sticky right-0 top-0 z-[2] cursor-pointer"
        >
          <X
            id="popup-close"
            className="absolute top-4 right-4 size-4 text-muted-foreground hover:text-foreground"
          />
        </div>
      )}
      {title && (
        <div className="mb-2 px-1 pt-6 text-center leading-[18px] font-bold tracking-[0.3px] text-foreground">
          {title}
        </div>
      )}
      {typeof children === 'string' ? (
        <div className="mx-auto max-w-[70%] px-4 text-center leading-[18px] text-muted-foreground">
          {children}
        </div>
      ) : (
        children
      )}
      {(positiveText || negativeText) && (
        <div className="mt-[30px] flex justify-center [&>button:first-child]:mr-2.5 [&>button]:w-[84px] max-[743px]:justify-between max-[743px]:[&>button:first-child]:mr-0 max-[743px]:[&>button]:w-[10.125rem]">
          <Button variant="secondary" onClick={onHandleCancel}>
            {negativeText}
          </Button>
          <Button onClick={onDone}>{positiveText}</Button>
        </div>
      )}
    </Backdoor>
  );
};

export default Popup;
